import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { addToast } from '@heroui/react'
import axios from 'axios'
import { usePostHog } from '@posthog/react'
import { useNavigate } from 'react-router'
import {
  ReturnsExchangeOptionsExchangeFlowEnum,
  ReturnsExchangeOptionsResponseOneOnOneVariantInfo,
  ReturnsLineItem
} from '@itsrever/rever-api'
import { RefundAction, ItemToReturn } from '@itsrever/returns-api-types'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { usePages } from '@/hooks'
import { useExchangeOptions } from '@/hooks/use-exchange-options'
import {
  addReturnedItem,
  ItemActions,
  addNewItem,
  ExchangedItemType
} from '@/redux/return/return-slice'
import { calculateUnitPrice } from '../helpers/calculate-unit-price'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { ItemExchange } from './item-exchange'
import { ItemReturnReasons, ReturnReasonData } from './item-return-reasons'
import { ItemProductRecommendations } from './item-product-recommendations'
import { XMarkIcon } from '@/ui/icons/xmark'
import { hasVariantStock } from '@/app/full-catalog/helpers/has-variant-stock'
import { CLAIMABLE_REASONS, isWFPOrder } from '@/utils/worry-free-purchase'

enum ItemFlowStep {
  RETURN_REASONS = 'RETURN_REASONS',
  EXCHANGE = 'EXCHANGE',
  PRODUCT_RECOMMENDATIONS = 'PRODUCT_RECOMMENDATIONS'
}

interface ItemOptionsProps {
  lineItem: ReturnsLineItem
  subProductId: number
  onClose: () => void
}

export const ItemFlow = ({
  lineItem,
  subProductId,
  onClose
}: ItemOptionsProps) => {
  const {
    i18n: { language },
    t
  } = useTranslation()

  const posthog = usePostHog()

  const navigate = useNavigate()

  const { pages } = usePages()

  const dispatch = useAppDispatch()

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const order = useAppSelector((store) => store.returnsApi.order.response)

  const moneyFormat = useAppSelector(
    (store) => store.returnsApi.order.response?.order?.money_format
  )

  const checkout_id = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const [currentItemSelection, setCurrentItemSelection] =
    useState<ItemToReturn>({
      line_item_id: lineItem.id as string,
      product_return_reason: '',
      return_reason_comment: '',
      quantity: 1,
      action: RefundAction.RefundAction_NO_ACTION
    })

  const [currentStep, setCurrentStep] = useState<ItemFlowStep>(
    ItemFlowStep.RETURN_REASONS
  )

  const { mutateAsync, data: exchangeOptions } = useExchangeOptions()

  const image = lineItem.product?.images?.[0]

  const price = calculateUnitPrice(
    { total: lineItem.total, quantity: lineItem.quantity },
    { amount_multiplied_by: moneyFormat?.amount_multiplied_by }
  )

  const creditAmount = price * currentItemSelection.quantity

  const refundAmount =
    currentItemSelection.action === RefundAction.RefundAction_TO_RETURN
      ? creditAmount
      : null

  const onSelectReturnReason = async ({
    return_reason,
    return_reason_comment,
    quantity
  }: ReturnReasonData) => {
    if (!ecommerceId || !checkout_id) {
      addToast({
        title: t('toast_errors.something_went_wrong'),
        description: t('toast_errors.confirmation_503')
      })
      return
    }

    try {
      const response = await mutateAsync({
        ecommerceId,
        returnsExchangeOptionsRequest: {
          checkout_id,
          ecommerce_id: ecommerceId,
          line_item_platform_id: lineItem.id,
          return_reason,
          quantity,
          language
        }
      })

      const updatedSelection: ItemToReturn = {
        ...currentItemSelection,
        quantity,
        product_return_reason: return_reason,
        return_reason_comment,
        action: RefundAction.RefundAction_TO_RETURN
      }

      setCurrentItemSelection(updatedSelection)

      // Every branch below books the same returned item; only the next step differs.
      const itemToReturn = {
        item: updatedSelection,
        lineItemId: lineItem.id ?? '',
        productId: lineItem.product?.id,
        subProductId,
        promotionalExchange: response.promotional_exchange,
        action: ItemActions.ToReturn
      }

      const exchangeFlowType = response.exchange_options?.exchange_flow
      const variants = response.line_item_product_info?.variants
      const hasVariants = variants?.some(
        (variant) => variant.is_available && hasVariantStock(variant)
      )

      if (
        isWFPOrder(order?.order?.line_items ?? []) &&
        CLAIMABLE_REASONS.includes(return_reason)
      ) {
        dispatch(addReturnedItem(itemToReturn))

        onClose()
        return
      }

      if (
        exchangeFlowType === ReturnsExchangeOptionsExchangeFlowEnum.OneOnOne
      ) {
        if (hasVariants) {
          setCurrentStep(ItemFlowStep.EXCHANGE)
        } else {
          dispatch(addReturnedItem(itemToReturn))
          onClose()
        }
        return
      }

      if (exchangeFlowType === ReturnsExchangeOptionsExchangeFlowEnum.Full) {
        posthog?.capture('Selected item has FC', {
          checkout_id
        })

        if (hasVariants) {
          setCurrentStep(ItemFlowStep.EXCHANGE)
        } else {
          dispatch(addReturnedItem(itemToReturn))
          setCurrentStep(ItemFlowStep.PRODUCT_RECOMMENDATIONS)
        }
        return
      }

      dispatch(addReturnedItem(itemToReturn))

      onClose()
      return
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 419)
      ) {
        addToast({
          description: t('toast_errors.error_419')
        })

        navigate({
          pathname: pages.Landing
        })

        return
      }

      addToast({
        title: t('toast_errors.something_went_wrong'),
        description: t('toast_errors.confirmation_503')
      })
    }
  }

  const onContinueVariantExchange = (
    variant?: ReturnsExchangeOptionsResponseOneOnOneVariantInfo
  ) => {
    const action = variant
      ? RefundAction.RefundAction_TO_EXCHANGE
      : RefundAction.RefundAction_TO_RETURN

    setCurrentItemSelection((prev) => ({
      ...prev,
      action
    }))

    dispatch(
      addReturnedItem({
        item: { ...currentItemSelection, action },
        lineItemId: lineItem.id ?? '',
        productId: lineItem.product?.id,
        subProductId,
        promotionalExchange: exchangeOptions?.promotional_exchange,
        action: variant ? ItemActions.ToExchangeOneOnOne : ItemActions.ToReturn
      })
    )

    if (variant) {
      posthog.capture('Exchange OneOnOne Selected', {
        checkout_id
      })

      dispatch(
        addNewItem({
          exchangeType: ExchangedItemType.ONE_ON_ONE,
          returnedItemId: currentItemSelection.line_item_id,
          quantity: currentItemSelection.quantity,
          price: (lineItem.total ?? 0) / (lineItem.quantity ?? 1),
          signedProductRaw: exchangeOptions?.signed_line_item_product_raw ?? '',
          productVariant: {
            productId: exchangeOptions?.line_item_product_info?.platform_id,
            variantId: variant.platform_id ?? '',
            productName: lineItem.name ?? '',
            variantName: variant.name ?? '',
            imageUrl: image?.src ?? '',
            inventoryQuantity: variant.inventory_quantity ?? 1
          }
        })
      )
    } else {
      posthog.capture('Refund clicked', { checkout_id })
    }

    if (
      exchangeOptions?.exchange_options?.exchange_flow ===
      ReturnsExchangeOptionsExchangeFlowEnum.Full
    ) {
      setCurrentStep(ItemFlowStep.PRODUCT_RECOMMENDATIONS)
    } else {
      onClose()
    }
  }

  if (currentStep === ItemFlowStep.PRODUCT_RECOMMENDATIONS) {
    return (
      <ItemProductRecommendations
        onClose={onClose}
        refundAmount={refundAmount}
        returnedItem={{
          product_id: lineItem.product?.id ?? '',
          refund_amount: price,
          return_reason: currentItemSelection.product_return_reason,
          comment: currentItemSelection.return_reason_comment,
          variant_id: currentItemSelection.line_item_id
        }}
      />
    )
  }

  return (
    <div className="relative flex h-full max-h-full flex-col">
      <button
        onClick={onClose}
        className="absolute right-2 top-2 rounded-sm p-1 text-neutral-500 transition-colors hover:bg-neutral-100"
      >
        <XMarkIcon className="size-[18px]" />
      </button>
      <div className="border-b border-solid border-neutral-200 p-4 md:p-5">
        <div className="flex gap-3 rounded-md">
          <div className="relative h-24 w-20 shrink-0 overflow-hidden rounded-md md:h-28 md:w-24">
            {image ? (
              <img
                height={112}
                width={96}
                src={image.src}
                alt={image.alt}
                className="size-full object-cover"
              />
            ) : (
              <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
                <MediaImageIcon width={20} height={20} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1 py-1">
            <div>
              <p className="line-clamp-2 text-left text-sm font-normal">
                {lineItem.name}
              </p>
              <p className="text-xs font-normal text-neutral-500">
                {lineItem.variant_name}
              </p>
            </div>
            <p className="text-[11px] font-normal uppercase text-neutral-500">
              {lineItem.product?.sku}
            </p>
            <p className="mt-auto text-sm font-normal">
              {price.toLocaleString(language, {
                style: 'currency',
                currency: moneyFormat?.currency
              })}
            </p>
          </div>
        </div>
      </div>
      {currentStep === ItemFlowStep.RETURN_REASONS && (
        <ItemReturnReasons
          onContinue={onSelectReturnReason}
          lineItem={lineItem}
        />
      )}
      {currentStep === ItemFlowStep.EXCHANGE &&
        exchangeOptions?.line_item_product_info?.variants &&
        exchangeOptions?.exchange_options?.exchange_flow && (
          <ItemExchange
            variants={exchangeOptions.line_item_product_info.variants}
            exchangeFlow={exchangeOptions?.exchange_options?.exchange_flow}
            onClose={onClose}
            creditAmount={creditAmount}
            returnedItem={{
              item: {
                ...currentItemSelection,
                action: RefundAction.RefundAction_TO_RETURN
              },
              lineItemId: lineItem.id ?? '',
              productId: lineItem.product?.id,
              subProductId,
              promotionalExchange: exchangeOptions?.promotional_exchange,
              action: ItemActions.ToReturn
            }}
            onContinue={onContinueVariantExchange}
          />
        )}
    </div>
  )
}
